// ============================================================
// TMR Portal Admin — criar e editar uma taxa da empresa
// ============================================================
import { useState } from 'react';
import { ApiError } from '@tmr/api-client';
import { Btn } from '../../components/primitives';
import { ModalShell } from '../../components/ModalShell';
import { Notice } from '../../components/Notice';
import { SelectField, TextField } from '../../components/fields';
import { Icons } from '../../lib/icons';
import { rateUnitSuffix } from '../../data/rateFormat';
import { useCompanyProducts, useCreateRate, useRateCatalog, useUpdateRate } from '../../data/rates/queries';
import {
  emptyRateForm,
  toCreateRequest,
  toRateForm,
  toUpdateRequest,
  validateRateForm,
} from '../../data/rates/form';
import type { RateForm, RateFormErrors } from '../../data/rates/form';
import type { CompanyRate } from '../../data/rates/contract';

/** Props for {@link RateFormModal}. */
interface RateFormModalProps {
  companyId: string;
  /** The rate being edited, or null to create one. */
  rate: CompanyRate | null;
  onClose: () => void;
}

/**
 * The rate form.
 *
 * Two things it deliberately does not do. It does not check band overlap — that depends on every other rate
 * of the Empresa and on how the server pairs them, so it asks and shows the answer (`company.rate_overlap`)
 * rather than keeping a second copy of the rule. And it does not offer to move a rate between the Empresa
 * and one of its products: that is a different rate on a different legacy row, and the backend refuses it.
 */
export function RateFormModal({ companyId, rate, onClose }: RateFormModalProps) {
  const editing = rate !== null;
  const [form, setForm] = useState<RateForm>(() => (rate ? toRateForm(rate) : emptyRateForm()));
  const [errors, setErrors] = useState<RateFormErrors>({});

  const catalog = useRateCatalog();
  // Only while the form is open, and only for a new rate: the scope of an existing one is not editable.
  const products = useCompanyProducts(rate ? undefined : companyId);
  const create = useCreateRate(companyId);
  const update = useUpdateRate(companyId);
  const saving = create.isPending || update.isPending;
  const saveError = create.error ?? update.error;

  const set = <K extends keyof RateForm>(key: K, value: RateForm[K]) =>
    setForm((current) => ({ ...current, [key]: value }));

  const submit = async () => {
    const found = validateRateForm(form);
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    if (rate) await update.mutateAsync({ rateId: rate.id, request: toUpdateRequest(form) });
    else await create.mutateAsync(toCreateRequest(form));

    onClose();
  };

  const rateTypeOptions = (catalog.data?.rateTypes ?? []).map((type) => ({
    value: String(type.id),
    label: type.title,
  }));
  const engineOptions = (catalog.data?.creditEngineTypes ?? []).map((type) => ({
    value: String(type.id),
    label: type.title,
  }));

  const productOptions = (products.data?.items ?? []).map((product) => ({
    value: String(product.id),
    label: product.name,
  }));

  return (
    <ModalShell title={editing ? 'Editar taxa' : 'Nova taxa'} onClose={onClose}>
      <div style={{ display: 'grid', gap: 14 }}>
        {/* The vocabulary comes from the legacy, so a form that could not read it would be offering
            invented options — better to say the list is unavailable than to guess at it. */}
        {/* An error here left an enabled select with no options — a control that looks usable and cannot
            be used. It says what happened and offers the way out, and the select stays disabled meanwhile. */}
        {catalog.isError && (
          <Notice tone="warning">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
              <span>Não foi possível carregar os tipos de taxa agora.</span>
              <Btn
                variant="secondary"
                icon={Icons.swap}
                onClick={() => void catalog.refetch()}
                disabled={catalog.isFetching}
              >
                {catalog.isFetching ? 'Tentando…' : 'Tentar de novo'}
              </Btn>
            </div>
          </Notice>
        )}

        <SelectField
          label="Tipo da taxa"
          required
          value={form.rateTypeId}
          onChange={(value) => set('rateTypeId', value)}
          options={rateTypeOptions}
          placeholder={catalog.isPending ? 'Carregando…' : 'Escolha o tipo'}
          error={errors.rateTypeId}
          disabled={catalog.isPending || catalog.isError}
        />

        {engineOptions.length > 0 && (
          <SelectField
            label="Motor de crédito"
            value={form.creditEngineTypeId}
            onChange={(value) => set('creditEngineTypeId', value)}
            options={engineOptions}
            placeholder="Todos"
          />
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <TextField
            label="Parcela inicial"
            required
            inputMode="numeric"
            value={form.minInstallment}
            onChange={(value) => set('minInstallment', value)}
            error={errors.minInstallment}
          />
          <TextField
            label="Parcela final"
            required
            inputMode="numeric"
            value={form.maxInstallment}
            onChange={(value) => set('maxInstallment', value)}
            error={errors.maxInstallment}
          />
        </div>

        {/* The suffix is not a fixed "%": Taxa de Saque and Operação de Crédito are amounts in reais, and
            the rate type is what says which (15 §5). Until a type is chosen, no unit is claimed. */}
        <TextField
          label="Valor da taxa"
          required
          inputMode="decimal"
          value={form.standardValue}
          onChange={(value) => set('standardValue', value)}
          error={errors.standardValue}
          suffix={form.rateTypeId ? rateUnitSuffix(Number(form.rateTypeId)) : undefined}
        />

        {/* A picker, not a free-text id. Typing the number meant a wrong digit created a Launch rate on
            another Empresa's product, which then exported to taxas_produtor and started answering that
            product's pricing. The server refuses that now too, but the field is where it stops being
            possible (15 secao 9). */}
        {!editing && (
          products.isError ? (
            <TextField
              label="Produto (opcional)"
              inputMode="numeric"
              value={form.productId}
              onChange={(value) => set('productId', value)}
              error={errors.productId}
              placeholder="Não foi possível listar os lançamentos — informe o id"
            />
          ) : (
            <SelectField
              label="Lançamento (opcional)"
              value={form.productId}
              onChange={(value) => set('productId', value)}
              options={productOptions}
              error={errors.productId}
              placeholder={
                products.isPending
                  ? 'Carregando…'
                  : productOptions.length === 0
                    ? 'Esta empresa não tem lançamentos'
                    : 'Taxa da empresa (todos os lançamentos)'
              }
              disabled={products.isPending || productOptions.length === 0}
            />
          )
        )}

        {editing && (
          <Notice tone="neutral">
            O valor aqui é a taxa padrão. Uma taxa temporária vigente continua valendo — corrigir a taxa
            padrão não a encerra.
          </Notice>
        )}

        {saveError && (
          <Notice tone="danger">
            {saveError instanceof ApiError ? saveError.detail : 'Não foi possível salvar a taxa.'}
          </Notice>
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
        <Btn variant="secondary" onClick={onClose} disabled={saving}>
          Cancelar
        </Btn>
        <Btn variant="primary" icon={Icons.check} onClick={() => void submit()} disabled={saving}>
          {saving ? 'Salvando…' : 'Salvar taxa'}
        </Btn>
      </div>
    </ModalShell>
  );
}
